import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { RootErrorBoundary, Shell } from '..';
import { useAreModulesReady } from '../..';
import { NotFound } from '../../';
import { useHoistedRoutes, useRoutes } from '../../shell';
const BusinessLayout = React.lazy(() => import('../scenes/BusinessLayout').then(module => ({ default: module.BusinessLayout })));
const CustomerLayout = React.lazy(() => import('../scenes/CustomerLayout').then(module => ({ default: module.CustomerLayout })));
export function useAppRouter({ rootRoutes = [], shell = Shell.Business } = {}) {
    const isReady = useAreModulesReady();
    // Hack to reuse the same array reference through re-renders.
    const [memoizedRootRoutes] = useState(rootRoutes);
    const routes = useRoutes();
    const wrapManagedRoutes = useCallback((managedRoutes) => {
        return {
            path: '/',
            element: shell === Shell.Customer ? _jsx(CustomerLayout, {}) : _jsx(BusinessLayout, {}),
            children: [
                {
                    // Pathless route to set an error boundary inside the layout instead of outside.
                    // It's quite useful to prevent losing the layout when an unmanaged error occurs.
                    errorElement: _jsx(RootErrorBoundary, {}),
                    children: [
                        ...managedRoutes,
                        {
                            path: '*',
                            element: !isReady ? _jsx(_Fragment, { children: "Loading.." }) : _jsx(NotFound, {}),
                        },
                    ],
                },
            ],
        };
    }, [isReady, shell]);
    // Using the useHoistedRoutes hook allow routes hoisted by modules to be rendered at the root of the router instead of under the root layout.
    // To disallow the hoisting functionality, do not use this hook.
    const hoistedRoutes = useHoistedRoutes([...routes, ...memoizedRootRoutes], {
        wrapManagedRoutes,
        // Only the following routes can be hoisted by modules.
        allowedPaths: [],
    });
    const router = useMemo(() => {
        return createBrowserRouter([...hoistedRoutes]);
    }, [hoistedRoutes]);
    return router;
}
